/**
 * Long-press hook (touch + mouse).
 *
 * Surfaces a per-element action menu on press-and-hold. Used by
 * `GroupTopicRow` (per-topic menu) and `MessageBubble` (copy / fork /
 * read-aloud chips). Desktop additionally relies on hover.
 *
 * Two independent trigger paths, because neither alone is reliable:
 *
 *  1. A `delay`-ms hold timer (set on pointerdown, cancelled on
 *     move > `moveTolerance`, scroll, or pointerup). This is the snappy
 *     path and the only path for a mouse press-and-hold. Selectable message
 *     text opts out of this timer for mouse/touch/pen because firing a React
 *     update while native selection is starting makes selection feel random.
 *
 *  2. The native `contextmenu` event, used as the authoritative trigger
 *     for TOUCH. Android fires `contextmenu` at the end of a native
 *     long-press, and — crucially — it survives the message list
 *     remounting rows mid-press. Virtualized rows (Virtuoso) re-key and
 *     unmount/remount on every streaming reply or refetch; if that lands
 *     during a press, the pressed bubble unmounts, its cleanup kills the
 *     hold timer, and the replacement instance never saw the pointerdown.
 *     `contextmenu` fires on whichever instance exists at press-time, so
 *     it is immune to that race. `onLongPress` is idempotent, so the two
 *     paths racing just set the same state twice.
 *
 * Text selection (`allowTextSelection`, set by `MessageBubble`):
 *   Agent bubbles are `user-select: text`, so the same long-press is ALSO
 *   the user's native Android text-selection gesture (handles + the OS copy
 *   toolbar). That has to coexist with the chips. When the flag is set, the
 *   hold timer stays dormant and the `contextmenu` path owns the touch chips.
 *   That path NEVER calls `preventDefault()` and NEVER clears the range —
 *   either one cancels the selection the long-press just began. Non-text
 *   targets (topic / chat rows) keep the default, which suppresses the junk
 *   native menu and opens ours.
 *
 * Returns the bind props you should spread onto the target element.
 *
 * Pattern intentionally avoids React state updates while the press is
 * active — that would cause re-renders and break gesture continuity. Uses
 * refs throughout.
 */
import { useCallback, useEffect, useRef } from 'react'

export type LongPressBind = {
  onPointerDown: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: (e: React.PointerEvent) => void
  onPointerCancel: (e: React.PointerEvent) => void
  onPointerLeave: (e: React.PointerEvent) => void
  onContextMenu?: (e: React.MouseEvent) => void
}

export function useLongPress(
  onLongPress: (e: React.PointerEvent) => void,
  options: {
    delay?: number
    moveTolerance?: number
    cancelContextMenu?: boolean
    allowTextSelection?: boolean
  } = {},
): LongPressBind {
  const {
    delay = 450,
    moveTolerance = 8,
    cancelContextMenu = true,
    allowTextSelection = false,
  } = options

  const timerRef = useRef<number | null>(null)
  const startRef = useRef<{ x: number; y: number } | null>(null)
  const firedRef = useRef(false)

  const scrollAbortRef = useRef<(() => void) | null>(null)

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    startRef.current = null
    if (scrollAbortRef.current) {
      scrollAbortRef.current()
      scrollAbortRef.current = null
    }
  }, [])

  useEffect(() => () => clear(), [clear])

  return {
    onPointerDown: (e) => {
      // Ignore secondary buttons (right-click, middle-click).
      if (e.button !== 0) return
      // A press on selectable message text IS the user's native selection
      // gesture. Leave it to the browser: desktop gets CSS hover actions, and
      // Android/pen get action chips from the native contextmenu event after
      // selection has started. The timer is only for non-selectable rows.
      const nativeSelectionOnSelectableTarget =
        allowTextSelection &&
        (
          e.pointerType === 'mouse' ||
          e.pointerType === 'touch' ||
          e.pointerType === 'pen' ||
          (
            !e.pointerType &&
            typeof window !== 'undefined' &&
            window.matchMedia('(pointer: coarse)').matches
          )
        )
      if (nativeSelectionOnSelectableTarget) return
      firedRef.current = false
      startRef.current = { x: e.clientX, y: e.clientY }
      // Attach scroll abort to the nearest scrollable ancestor rather than
      // window — window-capture catches unrelated list re-paints and kills
      // the timer before the finger has held long enough.
      const scrollTarget = (() => {
        let el: Element | null = (e.currentTarget as HTMLElement).parentElement
        while (el) {
          const style = window.getComputedStyle(el)
          if (/auto|scroll/.test(style.overflow + style.overflowY)) return el
          el = el.parentElement
        }
        return window
      })()
      const onScroll = () => clear()
      scrollTarget.addEventListener('scroll', onScroll, { passive: true } as AddEventListenerOptions)
      scrollAbortRef.current = () => scrollTarget.removeEventListener('scroll', onScroll)
      const ev = e
      timerRef.current = window.setTimeout(() => {
        firedRef.current = true
        onLongPress(ev)
      }, delay)
    },
    onPointerMove: (e) => {
      if (!startRef.current) return
      const dx = e.clientX - startRef.current.x
      const dy = e.clientY - startRef.current.y
      if (Math.hypot(dx, dy) > moveTolerance) clear()
    },
    onPointerUp: () => clear(),
    // Do NOT clear on pointercancel. On Android Chrome, pointercancel fires
    // at ~350ms when user-select:text is present (browser taking over for
    // text selection), which is before our hold timer. Clearing here would
    // prevent the hold-timer path from ever firing on selectable text.
    // (The contextmenu path below is the real safety net for touch.)
    onPointerCancel: () => { /* intentionally not clearing */ },
    // Touch pointers implicitly capture, so a finger never "leaves" the
    // element mid-press — the only touch pointerleave is the spec-mandated
    // one fired right AFTER pointerup/pointercancel. Clearing on that one
    // would kill the timer prematurely. Drag-away cancellation for touch is
    // already covered by onPointerMove's moveTolerance. Mouse pointers
    // genuinely leave while held down, so keep clearing for them.
    onPointerLeave: (e) => {
      if (e.pointerType === 'mouse') clear()
    },
    onContextMenu: cancelContextMenu
      ? (e) => {
          const pointerType = (e.nativeEvent as PointerEvent).pointerType
          const isTouchLongPress =
            pointerType === 'touch' ||
            pointerType === 'pen' ||
            (!pointerType &&
              typeof window !== 'undefined' &&
              window.matchMedia('(pointer: coarse)').matches)
          if (isTouchLongPress) {
            if (allowTextSelection) {
              // Selectable target (agent bubble): this long-press is ALSO the
              // user's text-selection gesture. Let the OS own it — calling
              // preventDefault() or clearing the range here is exactly what
              // nukes the selection. We only guarantee the action chips are
              // up; the hold timer can be lost to a mid-press row remount, so
              // the contextmenu is the surviving safety net. No preventDefault.
              clear()
              onLongPress(e as unknown as React.PointerEvent)
              return
            }
            // Non-selectable target (topic / chat row): suppress the junk
            // native context menu and open ours. Survives row remounts the
            // same way; onLongPress is idempotent.
            e.preventDefault()
            clear()
            onLongPress(e as unknown as React.PointerEvent)
            return
          }
          // Mouse right-click: only suppress the native menu if our
          // press-and-hold timer already opened the menu, so the browser
          // menu and our chips don't appear at once.
          if (firedRef.current) {
            e.preventDefault()
            firedRef.current = false
          }
        }
      : undefined,
  }
}
